import { AuditOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons'
import InfoIcon from '@mui/icons-material/Info'
import SettingsIcon from '@mui/icons-material/Settings'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import AnimateButton from 'components/@extended/AnimateButton'
import Card from 'components/@extended/Card'
import { TextField } from 'components/@extended/TextField'
import ComponentField from 'components/ComponentField'
import SimpleTitle from 'components/SimpleTitle'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import { useGlobalState } from 'contexts/GlobalStateContext'
import { deleteWithToken, putWithToken } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { useGetComponentById, useGetComponentLabels } from 'hooks/useComponents'
import { useFetchOrganizationCoreSettings } from 'hooks/useOrganizations'
import { useSnackbar } from 'notistack'
import type { FC } from 'react'
import { startTransition, useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  ComponentSourceType,
  ComponentType,
  CompositeFunction,
  CompositeType,
  getInputFormatFromComponentType,
} from 'types/components'
import { ThemeMode } from 'types/config'

const EditComponent: FC = () => {
  const theme = useTheme()
  const { token, profile, isLoading: isLoadingProfile } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()
  const location = useLocation()
  const { purcentAppState } = useGlobalState()
  const { componentId } = useParams<{ componentId: string }>()

  const [selectedOrganization, setSelectedOrganization] = useState<string>('')
  const [label, setLabel] = useState<string>('')
  const [initialLabel, setInitialLabel] = useState<string>('')
  const [componentType, setComponentType] = useState<ComponentType>(ComponentType.COMPONENT_TYPE_INTEGER)
  const [isOrganizationLevel, setIsOrganizationLevel] = useState<boolean>(false)
  const [isTargetEditable, setIsTargetEditable] = useState<boolean>(true)
  const [restrictedVisibility, setRestrictedVisibility] = useState<boolean>(false)
  const [targetMin, setTargetMin] = useState<string>('')
  const [targetMax, setTargetMax] = useState<string>('')
  const [isFormValid, setIsFormValid] = useState<boolean>(false)
  const [isLabelUsed, setIsLabelUsed] = useState<boolean>(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false)
  const [openCancelDialog, setOpenCancelDialog] = useState(false)
  const [targetsToDelete, setTargetsToDelete] = useState(0)
  const [sourceType, setSourceType] = useState<ComponentSourceType>(ComponentSourceType.COMPONENT_SOURCE_MANUAL)
  const [compositeFunction, setCompositeFunction] = useState<CompositeFunction>(
    CompositeFunction.COMPOSITE_FUNCTION_NONE
  )
  const [compositeType, setCompositeType] = useState<CompositeType>(CompositeType.COMPOSITE_TYPE_SIMPLE)
  const [lowerIsBetter, setLowerIsBetter] = useState<boolean>(false)
  const [cumulativeTargets, setCumulativeTargets] = useState<boolean>(false)
  const [numericDecimalPlaces, setNumericDecimalPlaces] = useState<number>(2)
  const [precision, setPrecision] = useState<number>(2)
  const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false)

  const { coreSettings, isLoading: isLoadingCoreSettings } = useFetchOrganizationCoreSettings(
    selectedOrganization ? [selectedOrganization] : [],
    token!
  )

  const { labels, isLoading: isLoadingLabels } = useGetComponentLabels(token!, selectedOrganization)
  const {
    details,
    isLoading: isLoadingComponent,
    isError,
    refetch,
  } = useGetComponentById(token!, componentId!, !!componentId)

  useEffect(() => {
    if (details) {
      setSelectedOrganization(details.component.organizationId)
      setLabel(details.component.label)
      setInitialLabel(details.component.label)
      setComponentType(details.component.componentType)
      setIsOrganizationLevel(details.component.isOrganizationLevel)
      setIsTargetEditable(details.component.isTargetEditable)
      setRestrictedVisibility(details.component.restrictedVisibility)
      setTargetMin(details.component.targetMin)
      setTargetMax(details.component.targetMax)
      setSourceType(details.component.sourceType)
      setLowerIsBetter(details.component.lowerIsBetter || false)
      setCumulativeTargets(details.component.cumulativeTargets || false)
      setNumericDecimalPlaces(details.component.numericDecimalPlaces ?? 0)
      setPrecision(details.component.precision ?? 0)
      if (details.component.sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE) {
        try {
          const sourceInfoObj =
            typeof details.component.sourceInfo === 'string'
              ? JSON.parse(details.component.sourceInfo || '{}')
              : details.component.sourceInfo || {}
          setCompositeFunction(sourceInfoObj.compositeFunction || CompositeFunction.COMPOSITE_FUNCTION_NONE)
          setCompositeType(sourceInfoObj.compositeType || CompositeType.COMPOSITE_TYPE_SIMPLE)
        } catch (error) {
          console.error('Error parsing sourceInfo JSON:', error)
          setCompositeFunction(CompositeFunction.COMPOSITE_FUNCTION_NONE)
          setCompositeType(CompositeType.COMPOSITE_TYPE_SIMPLE)
        }
      }
    }
  }, [details])

  const validateForm = useCallback(() => {
    setIsFormValid(selectedOrganization !== '' && label !== '' && !isLabelUsed)
  }, [selectedOrganization, label, isLabelUsed])
  useEffect(() => {
    validateForm()
  }, [selectedOrganization, label, targetMin, targetMax, isLabelUsed, details, validateForm])

  useEffect(() => {
    if (labels.includes(label) && label !== initialLabel) {
      setIsLabelUsed(true)
    } else {
      setIsLabelUsed(false)
    }
  }, [label, labels, initialLabel])

  useEffect(() => {
    if (componentType === ComponentType.COMPONENT_TYPE_BOOL) {
      setTargetMin('true')
      setTargetMax('true')
    } else {
      setTargetMin('0')
      setTargetMax('0')
    }
  }, [componentType])

  // Auto-select Simple composite type when format is not percentage
  useEffect(() => {
    if (
      componentType !== ComponentType.COMPONENT_TYPE_PERCENTAGE &&
      compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX
    ) {
      setCompositeType(CompositeType.COMPOSITE_TYPE_SIMPLE)
    }
  }, [componentType, compositeType])

  // Auto-select Average function when composite type is Complex
  useEffect(() => {
    if (compositeType === CompositeType.COMPOSITE_TYPE_COMPLEX) {
      setCompositeFunction(CompositeFunction.COMPOSITE_FUNCTION_AVERAGE)
    }
  }, [compositeType])

  // Helper to check if component type is numeric (float or integer)
  const isNumericType = (type: ComponentType) =>
    type === ComponentType.COMPONENT_TYPE_FLOAT || type === ComponentType.COMPONENT_TYPE_INTEGER

  const handleSubmit = () => {
    const componentDTO = {
      rootOrganizationId: purcentAppState?.selectedOrganization?.rootOrganizationId || '',
      organizationId: selectedOrganization,
      ledgerAccountId: null,
      isOrganizationLevel,
      isTargetEditable,
      restrictedVisibility,
      targetMin,
      targetMax,
      label,
      format: getInputFormatFromComponentType(componentType),
      sourceType: sourceType,
      componentType,
      lowerIsBetter,
      cumulativeTargets: cumulativeTargets,
      numericDecimalPlaces: isNumericType(componentType) ? numericDecimalPlaces : undefined,
      precision: isNumericType(componentType) ? precision : undefined,
      sourceInfo: JSON.stringify({
        compositeFunction:
          sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE
            ? compositeFunction
            : CompositeFunction.COMPOSITE_FUNCTION_NONE,
        compositeType: sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE ? compositeType : undefined,
      }),
    }

    putWithToken(`components/${componentId}`, token!, componentDTO)
      .then(() => {
        enqueueSnackbar('Component updated successfully', { variant: 'success' })
        refetch()
      })
      .catch((error) => {
        const errorMessage = error.message || 'Failed to update component'
        enqueueSnackbar(errorMessage, { variant: 'error' })
      })
  }

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true)
  }

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false)
  }

  const handleConfirmDelete = () => {
    deleteWithToken(`components/${componentId}`, token!)
      .then(() => {
        enqueueSnackbar('Component deleted successfully', { variant: 'success' })
        startTransition(() => {
          navigate('/admin/components/manage')
        })
      })
      .catch((error) => {
        const errorMessage = error.message || 'Failed to delete component'
        enqueueSnackbar(errorMessage, { variant: 'error' })
      })
    handleCloseDeleteDialog()
  }

  const handleUpdateClick = useCallback(() => {
    let targetsCount = 0
    if (
      details &&
      (componentType !== details.component.componentType ||
        isOrganizationLevel !== details.component.isOrganizationLevel)
    ) {
      targetsCount = details.approvedTargets
    }
    setTargetsToDelete(targetsCount)
    setOpenUpdateDialog(true)
  }, [componentType, isOrganizationLevel, details])

  const handleCloseUpdateDialog = () => {
    setOpenUpdateDialog(false)
  }

  const handleConfirmUpdate = () => {
    handleSubmit()
    handleCloseUpdateDialog()
  }

  const handleCancelConfirmation = () => {
    setOpenCancelDialog(false)
    startTransition(() => {
      navigate('/admin/components/manage')
    })
  }

  if (isLoadingComponent || isLoadingProfile || isLoadingCoreSettings || isLoadingLabels) {
    return <CircularProgress />
  }

  if (isError || !details) {
    return <Box>Error loading component</Box>
  }

  const hasTemplatesOrPlans = details.templates.length > 0 || details.plans.length > 0

  const labelBGColor = theme.palette.background.paper

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box>
          <SimpleTitle title="Edit Component" />
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle2" color="textSecondary">
              Approved Targets: {details.approvedTargets}
            </Typography>
            <Box mx={2}>
              <Typography variant="subtitle2" color="textSecondary">
                |
              </Typography>
            </Box>
            <Typography variant="subtitle2" color="textSecondary">
              Approved Actuals: {details.approvedMetrics}
            </Typography>
          </Box>
        </Box>
        <Tooltip
          title={
            hasTemplatesOrPlans
              ? 'A component can only be deleted if there are no templates or plans associated with it.'
              : 'Delete Component'
          }
        >
          <span>
            <IconButton onClick={handleDeleteClick} disabled={hasTemplatesOrPlans} color="error">
              <DeleteOutlined style={{ fontSize: '1.5rem' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={3}>
          {/* Left Column: Tables */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h5" gutterBottom>
              Templates using this Component
            </Typography>
            <Card sx={{ p: 2, mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Label</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.templates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell>{template.label}</TableCell>
                      <TableCell>{template.periodLabel}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit Template">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              startTransition(() => {
                                navigate(`/admin/templates/${template.id}/edit`)
                              })
                            }}
                          >
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            <Typography variant="h5" gutterBottom>
              Employee Plans using this Component
            </Typography>
            <Card sx={{ p: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {details.plans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>{`${plan.firstName} ${plan.lastName}`}</TableCell>
                      <TableCell>{plan.periodLabel}</TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center">
                          <Tooltip title="View Plan">
                            <IconButton
                              color="primary"
                              onClick={() => {
                                startTransition(() => {
                                  navigate(`/employee/${plan.userId}/compplan`)
                                })
                              }}
                            >
                              <EyeOutlined />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Plan">
                            <IconButton
                              color="primary"
                              onClick={() => {
                                startTransition(() => {
                                  navigate(`/create-comp-plan?userId=${plan.userId}`, {
                                    state: { from: location.pathname },
                                  })
                                })
                              }}
                            >
                              <EditOutlined />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Approval Batch">
                            <IconButton
                              color="primary"
                              onClick={() => {
                                startTransition(() => {
                                  navigate(`/admin/approvals/${plan.approvalBatchId}/edit`)
                                })
                              }}
                            >
                              <AuditOutlined />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </Grid>

          {/* Right Column: Form */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h5" gutterBottom>
              Component Details
            </Typography>
            <Card variant="outlined" sx={{ p: 4 }}>
              <Grid container spacing={3}>
                {/* Row 1: Name */}
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Name *"
                    key="label-field"
                    variant="outlined"
                    pill
                    shrink
                    fullWidth
                    value={label}
                    onChange={(e) => {
                      setLabel(e.target.value)
                      validateForm()
                    }}
                    error={isLabelUsed}
                    helperText={isLabelUsed ? 'Label is already used' : ''}
                    disabled
                  />
                </Grid>

                {/* Row 2: Select Team */}
                <Grid size={{ xs: 12 }}>
                  <SingleOrganizationSelector
                    userProfile={profile!}
                    selectedOrganizationId={selectedOrganization}
                    handleChange={(value) => {
                      setSelectedOrganization(value)
                      validateForm()
                    }}
                    label="Select Organization *"
                    labelBackgroundColor={labelBGColor}
                    disabled
                  />
                </Grid>

                {/* Row 3: Type */}
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                      Type *
                    </InputLabel>
                    <Select
                      id="component-type-select"
                      value={isOrganizationLevel}
                      onChange={(e) => setIsOrganizationLevel(e.target.value === 'true')}
                      fullWidth
                      variant="outlined"
                      style={{ borderRadius: '21px' }}
                      disabled
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            '& .MuiMenuItem-root:hover': {
                              backgroundColor:
                                theme.palette.mode === ThemeMode.DARK
                                  ? theme.palette.primary.darker
                                  : theme.palette.secondary.dark,
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value="true">Team</MenuItem>
                      <MenuItem value="false">Individual</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Row 4: Format with gear icon */}
                <Grid size={{ xs: 12 }}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                      Format *
                    </InputLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Select
                        id="component-format-select"
                        value={componentType}
                        onChange={(e) => setComponentType(e.target.value as ComponentType)}
                        fullWidth
                        variant="outlined"
                        style={{ borderRadius: '21px' }}
                        disabled
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              '& .MuiMenuItem-root:hover': {
                                backgroundColor:
                                  theme.palette.mode === ThemeMode.DARK
                                    ? theme.palette.primary.darker
                                    : theme.palette.secondary.dark,
                              },
                            },
                          },
                        }}
                      >
                        <MenuItem value={ComponentType.COMPONENT_TYPE_FLOAT}>Numeric</MenuItem>
                        <MenuItem value={ComponentType.COMPONENT_TYPE_INTEGER}>Numeric (Legacy)</MenuItem>
                        <MenuItem value={ComponentType.COMPONENT_TYPE_CURRENCY}>Currency</MenuItem>
                        <MenuItem value={ComponentType.COMPONENT_TYPE_PERCENTAGE}>Percentage</MenuItem>
                        <MenuItem value={ComponentType.COMPONENT_TYPE_BOOL}>Boolean</MenuItem>
                      </Select>
                      <Tooltip title="Advanced Settings">
                        <span>
                          <IconButton
                            onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                            disabled={componentType === ComponentType.COMPONENT_TYPE_BOOL}
                            color={showAdvancedSettings ? 'primary' : 'default'}
                          >
                            <SettingsIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </FormControl>
                </Grid>

                {/* Advanced Settings - Only show if showAdvancedSettings is true and not Boolean */}
                {showAdvancedSettings && componentType !== ComponentType.COMPONENT_TYPE_BOOL && (
                  <>
                    {/* Row 5: Source Type */}
                    <Grid size={{ xs: 12 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                          Source Type *
                        </InputLabel>
                        <Select
                          id="component-source-type-select"
                          value={sourceType}
                          onChange={(e) => {
                            setSourceType(e.target.value as ComponentSourceType)
                          }}
                          fullWidth
                          variant="outlined"
                          style={{ borderRadius: '21px' }}
                          disabled
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                '& .MuiMenuItem-root:hover': {
                                  backgroundColor:
                                    theme.palette.mode === ThemeMode.DARK
                                      ? theme.palette.primary.darker
                                      : theme.palette.secondary.dark,
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value={ComponentSourceType.COMPONENT_SOURCE_MANUAL}>Standard</MenuItem>
                          <MenuItem value={ComponentSourceType.COMPONENT_SOURCE_COMPOSITE}>Composite</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Row 6: Decimal Places (Display) and Decimal Places (Precision) */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                          Decimal Places (Display)
                        </InputLabel>
                        <Select
                          id="numeric-decimal-places-select"
                          value={numericDecimalPlaces}
                          onChange={(e) => setNumericDecimalPlaces(Number(e.target.value))}
                          fullWidth
                          variant="outlined"
                          style={{ borderRadius: '21px' }}
                          disabled={
                            componentType !== ComponentType.COMPONENT_TYPE_FLOAT &&
                            componentType !== ComponentType.COMPONENT_TYPE_INTEGER &&
                            componentType !== ComponentType.COMPONENT_TYPE_CURRENCY &&
                            componentType !== ComponentType.COMPONENT_TYPE_PERCENTAGE
                          }
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                '& .MuiMenuItem-root:hover': {
                                  backgroundColor:
                                    theme.palette.mode === ThemeMode.DARK
                                      ? theme.palette.primary.darker
                                      : theme.palette.secondary.dark,
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value={0}>0</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                          Decimal Places (Precision)
                        </InputLabel>
                        <Select
                          id="numeric-precision-select"
                          value={precision}
                          onChange={(e) => setPrecision(Number(e.target.value))}
                          fullWidth
                          variant="outlined"
                          style={{ borderRadius: '21px' }}
                          disabled={
                            componentType !== ComponentType.COMPONENT_TYPE_FLOAT &&
                            componentType !== ComponentType.COMPONENT_TYPE_INTEGER &&
                            componentType !== ComponentType.COMPONENT_TYPE_CURRENCY &&
                            componentType !== ComponentType.COMPONENT_TYPE_PERCENTAGE
                          }
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                '& .MuiMenuItem-root:hover': {
                                  backgroundColor:
                                    theme.palette.mode === ThemeMode.DARK
                                      ? theme.palette.primary.darker
                                      : theme.palette.secondary.dark,
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem value={0}>0</MenuItem>
                          <MenuItem value={1}>1</MenuItem>
                          <MenuItem value={2}>2</MenuItem>
                          <MenuItem value={3}>3</MenuItem>
                          <MenuItem value={4}>4</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Row 7: Composite Function, Composite Type (Only Show row if source type is composite) */}
                    {sourceType === ComponentSourceType.COMPONENT_SOURCE_COMPOSITE && (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                              Composite Function *
                            </InputLabel>
                            <Select
                              id="component-composite-function-select"
                              value={compositeFunction}
                              onChange={(e) => setCompositeFunction(e.target.value as CompositeFunction)}
                              fullWidth
                              variant="outlined"
                              style={{ borderRadius: '21px' }}
                              disabled
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    '& .MuiMenuItem-root:hover': {
                                      backgroundColor:
                                        theme.palette.mode === ThemeMode.DARK
                                          ? theme.palette.primary.darker
                                          : theme.palette.secondary.dark,
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_AVERAGE}>Average</MenuItem>
                              <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_MEDIAN}>Median</MenuItem>
                              <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_MODE}>Mode</MenuItem>
                              <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_COUNT}>Count</MenuItem>
                              <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_STDDEV}>
                                Standard Deviation
                              </MenuItem>
                              <MenuItem value={CompositeFunction.COMPOSITE_FUNCTION_SUM}>Sum</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                              Composite Type *
                            </InputLabel>
                            <Select
                              id="component-composite-type-select"
                              value={compositeType}
                              onChange={(e) => setCompositeType(e.target.value as CompositeType)}
                              fullWidth
                              variant="outlined"
                              style={{ borderRadius: '21px' }}
                              disabled
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    '& .MuiMenuItem-root:hover': {
                                      backgroundColor:
                                        theme.palette.mode === ThemeMode.DARK
                                          ? theme.palette.primary.darker
                                          : theme.palette.secondary.dark,
                                    },
                                  },
                                },
                              }}
                            >
                              <MenuItem value={CompositeType.COMPOSITE_TYPE_SIMPLE}>Simple</MenuItem>
                              <MenuItem
                                value={CompositeType.COMPOSITE_TYPE_COMPLEX}
                                disabled={componentType !== ComponentType.COMPONENT_TYPE_PERCENTAGE}
                              >
                                Complex
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {/* Row 8: Lower Is Better, Cumulative Targets */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                          Lower Is Better *
                        </InputLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Select
                            id="component-lower-is-better-select"
                            value={lowerIsBetter}
                            onChange={(e) => setLowerIsBetter(e.target.value === 'true')}
                            fullWidth
                            variant="outlined"
                            style={{ borderRadius: '21px' }}
                            disabled
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  '& .MuiMenuItem-root:hover': {
                                    backgroundColor:
                                      theme.palette.mode === ThemeMode.DARK
                                        ? theme.palette.primary.darker
                                        : theme.palette.secondary.dark,
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                          <Tooltip
                            title="Enable this when a lower value is considered better performance. For example, 'Number of Errors' or 'Response Time' where lower values indicate better results."
                            placement="right"
                          >
                            <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                          </Tooltip>
                        </Box>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel shrink variant="outlined" sx={{ backgroundColor: labelBGColor }}>
                          Cumulative Targets *
                        </InputLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Select
                            id="component-prorate-targets-select"
                            value={cumulativeTargets}
                            onChange={(e) => setCumulativeTargets(e.target.value === 'true')}
                            fullWidth
                            variant="outlined"
                            style={{ borderRadius: '21px' }}
                            disabled
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  '& .MuiMenuItem-root:hover': {
                                    backgroundColor:
                                      theme.palette.mode === ThemeMode.DARK
                                        ? theme.palette.primary.darker
                                        : theme.palette.secondary.dark,
                                  },
                                },
                              },
                            }}
                          >
                            <MenuItem value="true">Yes</MenuItem>
                            <MenuItem value="false">No</MenuItem>
                          </Select>
                          <Tooltip
                            title="Enable cumulative for targets. Automatically set based on component type (Integer, Currency, Float components are cumulative by default)."
                            placement="right"
                          >
                            <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                          </Tooltip>
                        </Box>
                      </FormControl>
                    </Grid>

                    {/* Row 9: Target Min and Target Max (Optional) */}
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ComponentField
                          componentType={componentType}
                          label="Target Min"
                          value={targetMin}
                          onChange={(value) => {
                            setTargetMin(value)
                            validateForm()
                          }}
                          name="targetMin"
                          disabled={!selectedOrganization || isLoadingCoreSettings}
                          currency={coreSettings?.[0]?.currency || 'USD'}
                          currencyDecimals={coreSettings?.[0]?.currencyDecimals || 0}
                          decimalPlaces={precision}
                          variant="outlined"
                          pill
                          shrink
                        />
                        <Tooltip
                          title="Optional: Used as a suggestion when creating plans. Leave blank if not needed."
                          placement="right"
                        >
                          <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ComponentField
                          componentType={componentType}
                          label="Target Max"
                          value={targetMax}
                          onChange={(value) => {
                            setTargetMax(value)
                            validateForm()
                          }}
                          name="targetMax"
                          disabled={!selectedOrganization || isLoadingCoreSettings}
                          currency={coreSettings?.[0]?.currency || 'USD'}
                          currencyDecimals={coreSettings?.[0]?.currencyDecimals || 0}
                          decimalPlaces={precision}
                          variant="outlined"
                          pill
                          shrink
                        />
                        <Tooltip
                          title="Optional: Used as a suggestion when creating plans. Leave blank if not needed."
                          placement="right"
                        >
                          <InfoIcon fontSize="small" sx={{ ml: 1, color: theme.palette.grey[500] }} />
                        </Tooltip>
                      </Box>
                    </Grid>
                  </>
                )}
              </Grid>
            </Card>

            {/* Action Buttons - Below the Card */}
            <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mt: 3 }}>
              <AnimateButton>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={() => {
                    setOpenCancelDialog(true)
                  }}
                >
                  Cancel
                </Button>
              </AnimateButton>
              <AnimateButton>
                <Button variant="contained" onClick={handleUpdateClick} disabled={!isFormValid}>
                  Update Component
                </Button>
              </AnimateButton>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirm Component Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this component?
            {(details.approvedTargets > 0 || details.approvedMetrics > 0) && (
              <Box mt={2}>
                <Typography color="error" fontWeight="bold">
                  Warning: The following will be permanently deleted:
                </Typography>
                {details.approvedTargets > 0 && (
                  <Typography color="error">
                    • {details.approvedTargets} approved target{details.approvedTargets > 1 ? 's' : ''}
                  </Typography>
                )}
                {details.approvedMetrics > 0 && (
                  <Typography color="error">
                    • {details.approvedMetrics} approved actual{details.approvedMetrics > 1 ? 's' : ''}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
        <DialogTitle>Confirm cancellation</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to cancel updating this component?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>No</Button>
          <Button onClick={handleCancelConfirmation} autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Confirmation Dialog */}
      <Dialog
        open={openUpdateDialog}
        onClose={handleCloseUpdateDialog}
        aria-labelledby="update-dialog-title"
        aria-describedby="update-dialog-description"
      >
        <DialogTitle id="update-dialog-title">Confirm Component Update</DialogTitle>
        <DialogContent>
          <DialogContentText id="update-dialog-description">
            Are you sure you want to update this component?
            {targetsToDelete > 0 && (
              <Typography color="error" mt={2}>
                Warning: {targetsToDelete} approved target
                {targetsToDelete > 1 ? 's' : ''} will be deleted due to changes in Component Type or Organization Level.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmUpdate} color="primary" autoFocus>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EditComponent
